import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  Paper,
  LinearProgress,
} from "@mui/material";
import { VariableSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useConfigDataService } from "../../services/ConfigDataService"; // Adjust path as needed

const columnDefs = [
  { key: "LocationCodeC1", label: "Location Code", flex: 1 },
  { key: "LocationC1", label: "Location Name", flex: 2 },
  { key: "Latitude", label: "Latitude", flex: 1 },
  { key: "Longitude", label: "Longitude", flex: 1 },
  { key: "Radius", label: "Geo-Radius", flex: 1 },
  { key: "RegionZone", label: "Region", flex: 1 },
  { key: "StateName", label: "State", flex: 1 },
  { key: "CityName", label: "City", flex: 1 },
];

const ROW_HEIGHT = 32;

const LocationMasterList = () => {
 const { fetchLocationMaster } = useConfigDataService();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState({});
  const [loading, setLoading] = useState(true);
  const listRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await fetchLocationMaster();
      setData(res || []);
    } catch (err) {
      console.error("Failed to fetch location data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      columnDefs.every(({ key }) => {
        const term = (search[key] || "").toLowerCase();
        if (!term) return true;
        const val = item[key];
        return val !== null &&
          val !== undefined &&
          val.toString().toLowerCase().includes(term);
      })
    );
  }, [data, search]);

  const Row = ({ index, style }) => {
    const row = filteredData[index];
    const adjustedStyle = { ...style, top: Math.floor(style.top) };

    return (
      <Box
        style={adjustedStyle}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          px: 0.8,
          fontSize: "0.875rem",
        }}
      >
        {columnDefs.map(({ key, flex }) => {
          const value = row[key];
          const display = value !== null && value !== undefined ? String(value) : "-";
          return (
            <Box
              key={key}
              sx={{
                flex,
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                pr: 1,
              }}
            >
              {display}
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LocationMaster");
    XLSX.writeFile(wb, "LocationMaster.xlsx");
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h2">Location Master</Typography>
        <Button variant="contained" onClick={handleExport}>Export to Excel</Button>
      </Box>

      <Divider sx={{ my: 1 }} />

      {loading ? (
        <LinearProgress />
      ) : (
        <Paper>
          <Box sx={{ display: "flex", p: 1, borderBottom: "1px solid #ccc" }}>
            {columnDefs.map(({ key, label, flex }) => (
              <Box key={key} sx={{ flex, pr: 1, minWidth: 0 }}>
                <TextField
                  label={label}
                  variant="standard"
                  value={search[key] || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
          </Box>

          <Box sx={{ height: "65vh" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  width={width}
                  itemCount={filteredData.length}
                  itemSize={() => ROW_HEIGHT}
                  itemKey={(index) => filteredData[index].LocationId || index}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default LocationMasterList;
